import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableContainer,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
  Link
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate, useLocation } from "react-router-dom";

import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";

const statusColor = (status) => {
  const x = String(status || "").toLowerCase();
  if (x === "en_revision") return "warning";
  if (x === "pagado" || x === "approved" || x === "completed") return "success";
  if (x === "rechazado" || x === "cancelado") return "error";
  return "default";
};

const statusLabel = (status) => {
  const x = String(status || "").toLowerCase();
  if (x === "esperando_comprobante") return "Esperando comprobante";
  if (x === "en_revision") return "En revisión";
  if (x === "pagado") return "Pagado";
  if (x === "rechazado") return "Rechazado";
  if (x === "cancelado") return "Cancelado";
  return status || "Pendiente";
};

const fmtMoney = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const AdminEcommerce = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const fetchOrders = useCallback(() => {
    fetch("/ecommerce/api/admin/orders/", { credentials: "include" })
      .then((resp) => resp.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : data?.results || data?.orders || [];
        setOrders(rows);
        setFilteredOrders(rows);
      })
      .catch(() => {
        setErrorMessage("No se pudieron cargar los pedidos de ecommerce.");
      });
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchOrders();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchOrders]);

  useEffect(() => {
    if (location.state && location.state.updated) {
      setSuccessMessage("Pedido actualizado con éxito.");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);

    const filtered = orders.filter((o) => {
      const ticket = String(o.ticket || "").toLowerCase();
      const cliente = String(o.razon_social || "").toLowerCase();
      const identificacion = String(o.identificacion || "").toLowerCase();
      const estado = String(o.estado || "").toLowerCase();
      const correo = String(o.correo || "").toLowerCase();
      const telefono = String(o.telefono || "").toLowerCase();
      return (
        ticket.includes(value) ||
        cliente.includes(value) ||
        identificacion.includes(value) ||
        estado.includes(value) ||
        correo.includes(value) ||
        telefono.includes(value)
      );
    });

    setFilteredOrders(filtered);
    setPage(0);
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else if (component === "AdminEcommerce") navigate("/admin-panel/ecommerce");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.removeItem("user_email");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_logged_in");
    navigate("/login");
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleDownloadProof = async (order) => {
    const proofUrl = order?.latest_proof_url || order?.proof_url || "";
    if (!proofUrl) {
      setErrorMessage("Este pedido aún no tiene comprobante cargado.");
      return;
    }

    try {
      const resp = await fetch(proofUrl, { credentials: "include" });
      if (!resp.ok) throw new Error();

      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${order.ticket || "comprobante"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setErrorMessage("No se pudo descargar el comprobante.");
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, transition: "all 0.3s ease" }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              bgcolor: "#FFFFFF"
            }}
          >
            <Typography variant="h6" mb={2}>
              Ecommerce
            </Typography>

            <TextField
              label="Buscar pedido"
              variant="outlined"
              fullWidth
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {filteredOrders.length === 0 ? (
              <Typography sx={{ color: "#666" }}>No hay pedidos registrados.</Typography>
            ) : (
              <>
                <TableContainer>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Ticket</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Cliente</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Documento</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Método pago</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Entrega</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Estado</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Fecha</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Total</TableCell>
                        <TableCell sx={{ bgcolor: "#1565C0", color: "#FFFFFF" }}>Comprobante</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredOrders
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((order) => (
                          <TableRow key={order.id || order.ticket}>
                            <TableCell>
                              <Link
                                component="button"
                                underline="hover"
                                sx={{ fontWeight: 600, color: "#1565C0" }}
                                onClick={() =>
                                  navigate(`/admin-panel/ecommerce/${order.ticket}`, {
                                    state: { order }
                                  })
                                }
                              >
                                {order.ticket}
                              </Link>
                            </TableCell>
                            <TableCell>{order.razon_social}</TableCell>
                            <TableCell>{order.identificacion}</TableCell>
                            <TableCell>
                              {String(order.metodo_pago || "")
                                .replaceAll("_", " ")
                                .toUpperCase()}
                            </TableCell>
                            <TableCell>
                              {String(order.metodo_entrega || "")
                                .replaceAll("_", " ")
                                .toUpperCase()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusLabel(order.estado)}
                                color={statusColor(order.estado)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell>{order.creado_en_formatted || order.creado_en || ""}</TableCell>
                            <TableCell>US$ {fmtMoney(order.total)}</TableCell>

                            <TableCell>
                              <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadProof(order)}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#1565C0",
                                  color: "#1565C0",
                                  "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" }
                                }}
                              >
                                Descargar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredOrders.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10]}
                />
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3500}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminEcommerce;